import { apiClient } from '@/utils/api-client'
import { cleanParams } from '@/utils/clean-params'

const API_INTERNAL_BASE = '/users'

/**
 * User service for managing account users.
 */
class UserService {
    /**
     * Create a new user.
     *
     * @param {Object} form - User form payload
     * @returns {Promise<Object>} Created user response
     */
    async create(form) {
        return await apiClient
            .post(API_INTERNAL_BASE, form)
            .then((response) => {
                return response.data
            })
    }

    /**
     * Remove a user by ULID.
     *
     * @param {string} userUlid - User ULID
     * @returns {Promise<Object>} Removal response
     */
    async remove(userUlid) {
        return await apiClient
            .delete(API_INTERNAL_BASE + '/' + userUlid)
            .then((response) => {
                return response.data
            })
    }

    /**
     * List all users for the current account.
     *
     * @returns {Promise<Object>} Users list response
     */
    async list() {
        return await apiClient.get(API_INTERNAL_BASE).then((response) => {
            return response.data
        })
    }

    /**
     * Search users for the current account, paginated for typeahead pickers.
     *
     * @param {Object} params - Query parameters
     * @param {string} [params.search] - Search term matched against name/email
     * @param {number} [params.per_page] - Results per page (1-50, default 20)
     * @param {string} [params.cursor] - Pagination cursor
     * @returns {Promise<Object>} Response with data[], meta
     */
    async search(params = {}) {
        return await apiClient
            .get(API_INTERNAL_BASE + '/search', {
                params: cleanParams(params),
            })
            .then((response) => response.data)
    }

    /**
     * Resend welcome email for a user.
     *
     * @param {string} userUlid - User ULID
     * @returns {Promise<Object>} Resend response
     */
    async resend(userUlid) {
        return await apiClient
            .post(API_INTERNAL_BASE + '/' + userUlid + '/welcome-email', null)
            .then((response) => {
                return response.data
            })
    }

    /**
     * Trigger a password reset email for a user.
     *
     * @param {string} userUlid - User ULID
     * @returns {Promise<Object>} Reset response
     */
    async reset(userUlid) {
        return await apiClient
            .post(API_INTERNAL_BASE + '/' + userUlid + '/password-reset', null)
            .then((response) => {
                return response.data
            })
    }

    /**
     * Update a user by ULID.
     *
     * @param {string} userUlid - User ULID
     * @param {Object} form - Update payload
     * @returns {Promise<Object>} Update response
     */
    async update(userUlid, form) {
        return await apiClient
            .put(API_INTERNAL_BASE + '/' + userUlid, form)
            .then((response) => {
                return response.data
            })
    }

    async listGrants(userUlid) {
        return await apiClient
            .get(API_INTERNAL_BASE + '/' + userUlid + '/grants')
            .then((response) => response.data)
    }

    /**
     * List direct grants for every user in the current account, in one request.
     *
     * @returns {Promise<Object>} Grants list response
     */
    async listAllGrants() {
        return await apiClient
            .get(API_INTERNAL_BASE + '/grants')
            .then((response) => response.data)
    }

    async createGrant(userUlid, form) {
        return await apiClient
            .post(API_INTERNAL_BASE + '/' + userUlid + '/grants', form)
            .then((response) => response.data)
    }

    async revokeGrant(userUlid, grantUlid) {
        return await apiClient
            .delete(API_INTERNAL_BASE + '/' + userUlid + '/grants/' + grantUlid)
            .then((response) => response.data)
    }

    async listTeams(userUlid) {
        return await apiClient
            .get(API_INTERNAL_BASE + '/' + userUlid + '/teams')
            .then((response) => response.data)
    }

    /**
     * List every team in the current account, with its members, in one request.
     *
     * @returns {Promise<Object>} Teams list response
     */
    async listAllTeams() {
        return await apiClient
            .get(API_INTERNAL_BASE + '/teams')
            .then((response) => response.data)
    }

    async addTeam(userUlid, teamUlid) {
        return await apiClient
            .post(
                API_INTERNAL_BASE + '/' + userUlid + '/teams/' + teamUlid,
                null
            )
            .then((response) => response.data)
    }

    async removeTeam(userUlid, teamUlid) {
        return await apiClient
            .delete(API_INTERNAL_BASE + '/' + userUlid + '/teams/' + teamUlid)
            .then((response) => response.data)
    }
}

export default new UserService()
